import { FC, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Icon } from '@iconify/react'
import { searchShipmentFullText, getShipmentGuideReport } from '../../services'
import { useAppSelector } from '../../store/hooks'
import filePdfO from '@iconify/icons-fa/file-pdf-o'
import { StoreShipmentInterface } from '../../interfaces'
import fileTextO from '@iconify/icons-fa/file-text-o'
import fileExcelO from '@iconify/icons-fa/file-excel-o'
import broomIcon from '@iconify/icons-mdi/broom'

import {
  PrinterIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import {
  exportShipmentsPDF,
  exportShipmentsTXT,
  exportShipmentsXLSX,
} from '../../utils/exports'
import {
  FormText,
  MainPage,
  PageTitle,
  FormSelect,
  ShipmentField,
  ShipmentTable,
  ShipmentFilters,
  PaginationFooter,
  ShipmentFilterEnum,
  ShipmentMultiPrintModal,
  ShipmentLabelDjangoModal,
  ConfirmModalStatus,
  ModalLoading,
  PrimaryButton,
  FormDatePicker,
  MixedFilterDisclosure,
  FormSimpleRadioGroup,
  SecondaryButton,
} from '../../components'
import Dropdown from '../../components/Atoms/Dropdown'
import type { MixedFilter } from '../../components'
import {
  INITIAL_DELIVERY_TYPES,
  INITIAL_OTHERSFILTERSHIPMENT,
  INITIAL_PAYMENT_MODES,
  INITIAL_SERVICES,
  INITIAL_STATUSES,
  ShipmentStatus,
} from './filterOptions'
import { PrintLabel } from '../../services/printerServices'
import { ShipmentLabelFormat } from '../../hooks/useShipmentLabel'
import { mergeSelection, toggleSelection } from '../../utils/shipmentSelection'
import { degradeBatchSize } from '../../utils/bulkPrint'
import moment from 'moment'

/**
 * Cuántas guías entran en un grupo de impresión, para bolsillo Y para etiquetas PDF.
 *
 * Los dos caminos mandaban TODA la selección en una sola petición y se quedaban sin
 * tiempo: el backend genera un documento (o una etiqueta) por guía, y tanto el .NET como
 * `apiDjango` cortan a los 30 s. El síntoma en etiquetas es "Error HTTP  al generar la
 * etiqueta." —sin número de status, que es la firma de un timeout de axios, no de un
 * error del servidor—; en bolsillo era una hoja en blanco sin explicación.
 *
 * 50 es empírico: 300 y 100 seguían dando timeout (reportado 2026-09-08). Mismo número
 * que el detalle de carga masiva, para que la regla sea una sola en toda la app.
 */
const PRINT_BATCH_LIMIT = 50

/**
 * A cuánto se baja el grupo cuando el servicio se queda sin tiempo.
 *
 * Cuánto aguanta el backend depende de la carga del momento y del tamaño de cada guía,
 * así que ante un timeout la pantalla vuelve a partir la selección con este valor y
 * reintenta sola, en vez de dejar al operador probando números. Si con 10 tampoco
 * genera, el problema no es el tamaño del grupo.
 */
const PRINT_BATCH_MIN = 10

const ConsultShipments: FC = () => {
  const user = useAppSelector((state) => state.user)

  const [page, setPage] = useState(0)
  const [query, setQuery] = useState('')
  const [searchConsigneeType, setSearchConsigneeType] = useState<1 | 2 | 3>(1)
  const [showFilters] = useState(false)
  const [registersNumber, setRegistersNumber] = useState(10)
  const [shipments, setShipments] = useState<StoreShipmentInterface[]>([])
  const [selecteds, setSelecteds] = useState<StoreShipmentInterface[]>([])
  const [filters, setFilters] = useState<Map<ShipmentFilterEnum, string>>(
    () => {
      const initial = new Map<ShipmentFilterEnum, string>()
      initial.set(
        ShipmentFilterEnum.START_DATE,
        moment().subtract(1, 'months').format('YYYY-MM-DD'),
      )
      initial.set(ShipmentFilterEnum.END_DATE, moment().format('YYYY-MM-DD'))
      return initial
    },
  )
  const [minDate, setMinDate] = useState<Date | null>(
    moment().subtract(1, 'months').toDate(),
  )
  const [maxDate, setMaxDate] = useState<Date | null>(moment().toDate())
  const [statusFilters, setStatusFilters] = useState(INITIAL_STATUSES)
  const [deliveryTypes, setDeliveryTypes] = useState(INITIAL_DELIVERY_TYPES)
  const [paymentModeFilters, setPaymentModeFilters] = useState(
    INITIAL_PAYMENT_MODES,
  )
  const [serviceFilters, setServiceFilters] = useState(INITIAL_SERVICES)
  const [filterSearchApi, setFilterSearchApi] = useState(false)
  const [fromfilterApi, setFromFilterApi] = useState<string>('')
  const [endfilterApi, setEndFilterApi] = useState<string>('')
  const [hasAutoSearchedFromUrl, setHasAutoSearchedFromUrl] = useState(false)
  // Auto-search por número de guía recibido en la URL (?query=...), ej. desde
  // "Ver guía" en el detalle de importación. Flag para dispararlo una sola vez.
  const [hasAutoSearchedQuery, setHasAutoSearchedQuery] = useState(false)
  const [selectPrint, setSelectPrint] = useState(false)
  // Grupo visible de cada visor. Ver PRINT_BATCH_LIMIT.
  const [pocketBatch, setPocketBatch] = useState(0)
  const [labelBatch, setLabelBatch] = useState(0)
  const [selectLabelDjango, setSelectLabelDjango] = useState(false)
  // Etiqueta pequeña y grande usan el mismo servicio Django (`POST /label`); la
  // única diferencia es el formato que se pide.
  const [labelDjangoFormat, setLabelDjangoFormat] =
    useState<ShipmentLabelFormat>('pdf')
  const [openPrintHelpModal, setOpenPrintHelpModal] = useState(false)
  const [printHelpFormat, setPrintHelpFormat] = useState(1)
  const [shipmentFilterType, setShipmentFilterType] = useState<number | null>(
    null,
  )
  const [otherFilters, setOtherFilters] = useState(INITIAL_OTHERSFILTERSHIPMENT)

  const searchPlaceholder =
    searchConsigneeType === 1
      ? 'Buscar por número de guía o por nombre/documento de remitente o destinatario'
      : searchConsigneeType === 2
        ? 'Buscar por número manifiesto '
        : 'Buscar por número de guia aliada'

  let [loading, setLoading] = useState(false)
  let [loadingStatus, setLoadingStatus] = useState(
    ConfirmModalStatus.PROCESSING,
  )
  let [loadingTitle, setLoadingTitle] = useState('Cargando...')

  const [sortBy, setSortBy] = useState({
    field: ShipmentField.SHIPMENT,
    asc: false,
  })

  const mixedFilters = useMemo<MixedFilter<any>[]>(() => {
    return [
      {
        title: 'Estado',
        isMultiple: true,
        multipleOptions: statusFilters,
        onSelectMultipleOption: setStatusFilters,
      },
      {
        title: 'Tipo de Entrega',
        isMultiple: true,
        multipleOptions: deliveryTypes,
        onSelectMultipleOption: setDeliveryTypes,
      },
      {
        title: 'Forma de Pago',
        isMultiple: true,
        multipleOptions: paymentModeFilters,
        onSelectMultipleOption: setPaymentModeFilters,
      },
      {
        title: 'Servicio',
        isMultiple: true,
        multipleOptions: serviceFilters,
        onSelectMultipleOption: setServiceFilters,
      },
      {
        title: 'Otros',
        isMultiple: true,
        multipleOptions: otherFilters,
        onSelectMultipleOption: setOtherFilters,
      },
    ]
  }, [
    statusFilters,
    deliveryTypes,
    paymentModeFilters,
    serviceFilters,
    otherFilters,
  ])

  const onSearchShipments = () => {
    setFilterSearchApi(true)
  }

  const paginatedShipments = useMemo(() => {
    const orderedShipments = shipments.sort((a, b) => {
      if (sortBy.field === ShipmentField.SHIPMENT) {
        return sortBy.asc
          ? Number(a.shipmentNumber) - Number(b.shipmentNumber)
          : Number(b.shipmentNumber) - Number(a.shipmentNumber)
      } else if (sortBy.field === ShipmentField.PIECES) {
        return sortBy.asc
          ? (a.totalPieces ?? 0) - (b.totalPieces ?? 0)
          : (b.totalPieces ?? 0) - (a.totalPieces ?? 0)
      } else if (sortBy.field === ShipmentField.WEIGHT) {
        return sortBy.asc
          ? (a.totalChargedWeight ?? 0) - (b.totalChargedWeight ?? 0)
          : (b.totalChargedWeight ?? 0) - (a.totalChargedWeight ?? 0)
      } else if (sortBy.field === ShipmentField.ALIAS) {
        return sortBy.asc
          ? a.consigneeName?.localeCompare(b.consigneeName ?? '')
          : b.consigneeName?.localeCompare(a.consigneeName ?? '')
      } else if (sortBy.field === ShipmentField.PHONE) {
        return sortBy.asc
          ? a.consigneePhone?.localeCompare(b.consigneePhone ?? '')
          : b.consigneePhone?.localeCompare(a.consigneePhone ?? '')
      } else if (sortBy.field === ShipmentField.CITY) {
        return sortBy.asc
          ? a.locationName?.localeCompare(b.locationName ?? '')
          : b.locationName?.localeCompare(a.locationName ?? '')
      } else if (sortBy.field === ShipmentField.DATE) {
        return (
          (sortBy.asc
            ? a.creationDate?.localeCompare(b.creationDate ?? '')
            : b.creationDate?.localeCompare(a.creationDate ?? '')) ?? 0
        )
      } else if (sortBy.field === ShipmentField.TRACKING) {
        return sortBy.asc
          ? a.statusName?.localeCompare(b?.statusName)
          : b.statusName?.localeCompare(a?.statusName)
      } else if (sortBy.field === ShipmentField.GUIDEALLIED) {
        return sortBy.asc
          ? a.shipmentReference?.localeCompare(b?.shipmentReference)
          : b.shipmentReference?.localeCompare(a?.shipmentReference)
      }

      return 0
    })

    const start = page * registersNumber
    const end = start + registersNumber

    return orderedShipments.slice(start, end)
  }, [shipments, sortBy, page, registersNumber])

  /**
   * Tamaño de grupo vigente. Baja solo si el servicio se queda sin tiempo, y no vuelve
   * a subir en la misma visita: si el backend viene lento, volver a 50 solo haría que el
   * operador se coma otro timeout de 30 s.
   */
  const [batchSize, setBatchSize] = useState(PRINT_BATCH_LIMIT)

  /** Grupos en los que se parte la selección para imprimir. */
  const printGroupCount = Math.max(1, Math.ceil(selecteds.length / batchSize))

  /**
   * El visor avisó timeout: se reparte más chico y se vuelve al primer grupo (los
   * índices viejos apuntaban a otro reparto). `false` = ya estaba en el piso.
   */
  const handleLabelTimeout = (): boolean => {
    const next = degradeBatchSize(batchSize, PRINT_BATCH_MIN)
    if (next === null) return false
    setBatchSize(next)
    setLabelBatch(0)
    setPocketBatch(0)
    return true
  }

  const handleSortByChange = (field: ShipmentField) => {
    if (sortBy.field === field) {
      setSortBy({ ...sortBy, asc: !sortBy.asc })
    } else {
      setSortBy({ field, asc: true })
    }
  }

  useEffect(() => {
    if (openPrintHelpModal) printLabelApi()

    setOpenPrintHelpModal(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPrintHelpModal])

  const printLabelApi = async () => {
    setLoadingStatus(ConfirmModalStatus.PROCESSING)
    setLoadingTitle('Imprimiendo...')
    setLoading(true)

    try {
      for (const a of selecteds) {
        await PrintLabel(
          Number(a?.shipmentNumber ?? '0'),
          parseInt(a?.serviceCode ?? '60'),
          a?.buCodeSource ?? '',
          [],
          printHelpFormat,
        )
      }

      setLoadingStatus(ConfirmModalStatus.SUCCESS)
      setLoadingTitle('Impresión completada')
    } catch (error) {
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle('Error al imprimir')
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilters = () => {
    const start = moment().startOf('month').toDate()
    const end = moment().toDate()

    setQuery('')
    setSearchConsigneeType(1)

    setMinDate(start)
    setMaxDate(end)

    setFilters(() => {
      const next = new Map<ShipmentFilterEnum, string>()
      next.set(
        ShipmentFilterEnum.START_DATE,
        moment(start).format('YYYY-MM-DD'),
      )
      next.set(ShipmentFilterEnum.END_DATE, moment(end).format('YYYY-MM-DD'))
      return next
    })

    setFromFilterApi(moment(start).format('YYYY-MM-DD'))
    setEndFilterApi(moment(end).format('YYYY-MM-DD'))

    setStatusFilters(INITIAL_STATUSES.map((opt) => ({ ...opt })))
    setDeliveryTypes(INITIAL_DELIVERY_TYPES.map((opt) => ({ ...opt })))
    setPaymentModeFilters(INITIAL_PAYMENT_MODES.map((opt) => ({ ...opt })))
    setServiceFilters(INITIAL_SERVICES.map((opt) => ({ ...opt })))
    setShipmentFilterType(null)

    setSelecteds([])
    setPage(0)
  }

  // Initialize filters from URL query parameters (from, to, status, dateField)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      const from = params.get('from')
      const to = params.get('to')
      const statusParam = params.get('status')
      const shipmentFilterTypeParam = params.get('shipmentFilterType')
      // const dateField = params.get('dateField') // Currently not used by API

      const isValidDate = (s: string) => moment(s, 'YYYY-MM-DD', true).isValid()
      let didUpdate = false

      if (shipmentFilterTypeParam) {
        setShipmentFilterType(Number(shipmentFilterTypeParam))
        didUpdate = true
      }

      if (from && isValidDate(from)) {
        const start = moment(from, 'YYYY-MM-DD').toDate()
        setMinDate(start)
        setFilters((prev) => {
          const next = new Map(prev)
          next.set(
            ShipmentFilterEnum.START_DATE,
            moment(start).format('YYYY-MM-DD'),
          )
          return next
        })
        setFromFilterApi(moment(start).format('YYYY-MM-DD'))
        didUpdate = true
      }

      if (to && isValidDate(to)) {
        const end = moment(to, 'YYYY-MM-DD').toDate()
        setMaxDate(end)
        setFilters((prev) => {
          const next = new Map(prev)
          next.set(
            ShipmentFilterEnum.END_DATE,
            moment(end).format('YYYY-MM-DD'),
          )
          return next
        })
        setEndFilterApi(moment(end).format('YYYY-MM-DD'))
        didUpdate = true
      }

      if (statusParam) {
        const statusNames = statusParam
          .split(',')
          .map((s) => s.trim().toUpperCase())
        const nameToEnum: Record<string, ShipmentStatus> = {
          DRAFT: ShipmentStatus.DRAFT,
          ACTIVE: ShipmentStatus.ACTIVE,
          ANNULLED: ShipmentStatus.ANNULLED,
          REPLACED: ShipmentStatus.REPLACED,
          DELIVERED: ShipmentStatus.DELIVERED,
        }
        const selectedValues = statusNames
          .map((n) => nameToEnum[n])
          .filter((v) => v !== undefined) as ShipmentStatus[]

        if (selectedValues.length > 0) {
          setStatusFilters((prev) =>
            prev.map((opt) => ({
              ...opt,
              checked: selectedValues.includes(opt.value),
            })),
          )
          didUpdate = true
        }
      }

      if (shipmentFilterTypeParam) {
        const typeVal = Number(shipmentFilterTypeParam)
        setShipmentFilterType(typeVal)
        setOtherFilters((prev) =>
          prev.map((opt) => ({
            ...opt,
            checked: opt.value === typeVal,
          })),
        )
        didUpdate = true
      }

      if (!didUpdate) {
        const path = window.location.pathname || ''
        if (
          path.endsWith('/shipments/consult') &&
          !params.has('shipmentFilterType')
        ) {
          const start = moment().subtract(1, 'years').startOf('year').toDate()
          const end = moment().toDate()

          setMinDate(start)
          setMaxDate(end)

          setFilters((prev) => {
            const next = new Map(prev)
            next.set(
              ShipmentFilterEnum.START_DATE,
              moment(start).format('YYYY-MM-DD'),
            )
            next.set(
              ShipmentFilterEnum.END_DATE,
              moment(end).format('YYYY-MM-DD'),
            )
            return next
          })

          setFromFilterApi(moment(start).format('YYYY-MM-DD'))
          setEndFilterApi(moment(end).format('YYYY-MM-DD'))
          didUpdate = true
        }
      }

      if (didUpdate) {
        setFilterSearchApi(true)
      }
    } catch {}
  }, [])

  useEffect(() => {
    const startDateFilter = filters.get(ShipmentFilterEnum.START_DATE)
    const endDateFilter = filters.get(ShipmentFilterEnum.END_DATE)

    if (startDateFilter) {
      const parsedStart = moment(startDateFilter).toDate()
      setMinDate((prev) =>
        prev?.getTime() === parsedStart.getTime() ? prev : parsedStart,
      )
    } else {
      setMinDate((prev) => (prev === null ? prev : null))
    }

    if (endDateFilter) {
      const parsedEnd = moment(endDateFilter).toDate()
      setMaxDate((prev) =>
        prev?.getTime() === parsedEnd.getTime() ? prev : parsedEnd,
      )
    } else {
      setMaxDate((prev) => (prev === null ? prev : null))
    }
  }, [filters])

  useEffect(() => {
    if (
      !hasAutoSearchedFromUrl &&
      (fromfilterApi || endfilterApi) &&
      (user?.client?.accountCode != null || user?.client?.id != null)
    ) {
      setFilterSearchApi(true)
      setHasAutoSearchedFromUrl(true)
    }
  }, [
    hasAutoSearchedFromUrl,
    fromfilterApi,
    endfilterApi,
    user?.client?.accountCode,
    user?.client?.id,
  ])

  // Auto-search por número de guía en la URL (?query=<nº de guía>). Llega desde el
  // botón "Ver guía" del detalle de importación. Setea el buscador en modo Guía,
  // llena el campo y dispara la búsqueda una sola vez (cuando el cliente ya cargó).
  // setQuery + setFilterSearchApi se batchean → el fetch lee el query ya actualizado.
  useEffect(() => {
    if (hasAutoSearchedQuery) return
    const userLoaded =
      user?.client?.accountCode != null || user?.client?.id != null
    if (!userLoaded) return
    const q = new URLSearchParams(window.location.search).get('query')
    if (!q) return
    setSearchConsigneeType(1) // 1 = Guía
    setQuery(q)
    setFilterSearchApi(true)
    setHasAutoSearchedQuery(true)
  }, [hasAutoSearchedQuery, user?.client?.accountCode, user?.client?.id])

  useEffect(() => {
    if (filterSearchApi) {
      const customercode =
        user?.client?.accountCode !== null
          ? '' + user?.client?.accountCode
          : undefined
      const customerId =
        user?.client?.id !== null ? '' + user?.client?.id : undefined
      if (!customercode && !customerId) return

      setLoadingStatus(ConfirmModalStatus.PROCESSING)
      setLoadingTitle('Cargando...')
      setLoading(true)

      const fetchShipments = async () => {
        let queryStr: string | undefined = undefined
        let manifestNumber: string | undefined = undefined
        let shipmentReference: string | undefined = undefined

        if (searchConsigneeType === 1) {
          queryStr = query
        } else if (searchConsigneeType === 2) {
          manifestNumber = query
        } else if (searchConsigneeType === 3) {
          shipmentReference = query
        }

        let startIso: string | undefined = undefined
        let endIso: string | undefined = undefined

        const isNoDateFilter =
          shipmentFilterType === 1 || shipmentFilterType === 3

        if (!isNoDateFilter) {
          startIso = fromfilterApi
            ? moment(fromfilterApi, 'YYYY-MM-DD').toISOString()
            : minDate?.toISOString()

          endIso = endfilterApi
            ? moment(endfilterApi, 'YYYY-MM-DD').add(1, 'days').toISOString()
            : maxDate
              ? moment(maxDate).add(1, 'days').toISOString()
              : undefined
        }

        const response: any = await searchShipmentFullText(
          queryStr ?? '',
          undefined,
          undefined,
          startIso,
          endIso,
          customercode,
          customercode == null ? customerId : undefined,
          manifestNumber,
          shipmentReference,
          4000,
          statusFilters.filter((s) => s.checked).map((s) => s.value),
          deliveryTypes.filter((d) => d.checked).map((d) => d.value),
          paymentModeFilters.filter((p) => p.checked).map((p) => p.value),
          serviceFilters.filter((s) => s.checked).map((s) => s.value),
          shipmentFilterType ?? undefined,
        )
        setLoading(false)

        if (!response.didError) setShipments(response.model ?? [])
      }

      fetchShipments()
      setFilterSearchApi(false)
    }
  }, [filterSearchApi, fromfilterApi, endfilterApi, user?.client?.id])

  const handleExportReport = async (
    format: 'HTML' | 'PDF' | 'EXCEL' = 'EXCEL',
  ) => {
    const codCliente = user.client?.accountCode ?? ''

    const rif = user.client?.identificationNumber
      ? user.client.identificationNumber
      : user.user?.identificationNumber || ''

    setLoading(true)
    setLoadingTitle(
      format === 'HTML' ? 'Generando reporte...' : 'Descargando reporte...',
    )
    setLoadingStatus(ConfirmModalStatus.PROCESSING)

    try {
      const response = await getShipmentGuideReport(
        codCliente,
        rif,
        moment(minDate).format('YYYY-MM-DD'),
        moment(maxDate).format('YYYY-MM-DD'),
        format,
      )

      if (format !== 'HTML') {
        const url = window.URL.createObjectURL(new Blob([response]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `Reporte_Guias_${moment().format('YYYYMMDDHHmmss')}.${
            format === 'PDF' ? 'pdf' : 'xlsx'
          }`,
        )
        document.body.appendChild(link)
        link.click()
        link.parentNode?.removeChild(link)
      }

      setLoadingStatus(ConfirmModalStatus.SUCCESS)
      setLoadingTitle('Archivo generado correctamente')
      setTimeout(() => setLoading(false), 500)
    } catch (error) {
      console.error(error)
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle('Error al procesar la solicitud')
      setTimeout(() => setLoading(false), 2000)
    }
  }

  return (
    <MainPage>
      <PageTitle title="Consulta de guías" />

      <div className="flex flex-1 flex-col ">
        {/* Filters */}
        <div className="flex flex-col w-full gap-4">
          <div className="flex flex-col xl:flex-row gap-4 xl:gap-6 w-full">
            {/* Buscador */}
            <div className="flex flex-col w-full xl:w-2/5 gap-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <label className="text-m font-medium text-gray-900 truncate">
                  Buscador
                </label>
                <FormSimpleRadioGroup<1 | 2 | 3>
                  horizontal
                  name="shipments-search-consignee-type"
                  selected={searchConsigneeType}
                  onSelectOption={(option) => setSearchConsigneeType(option)}
                  options={[
                    { value: 1, name: 'Guía' },
                    { value: 2, name: 'Nº Manifiesto' },
                    { value: 3, name: 'Nº Guía aliada' },
                  ]}
                  className="!mb-0 background-white"
                  wrapperClassName="justify-start sm:justify-end"
                />
              </div>

              <FormText
                value={query}
                id="shipments-search-query"
                name="shipments-search-query"
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                data-te-toggle="tooltip"
                title={searchPlaceholder}
                className="w-full"
              />
            </div>

            {/* Fechas y Filtros */}
            <div className="flex flex-col lg:flex-row gap-4 w-full xl:w-3/5">
              <div className="flex flex-col sm:flex-row gap-4 w-full">
                <div className="w-full">
                  <FormDatePicker
                    id="shipments-date-from"
                    name="shipments-date-from"
                    label="Desde"
                    labelClassname="mb-1"
                    useRange={false}
                    maxDate={maxDate ?? undefined}
                    value={{
                      startDate: minDate,
                      endDate: minDate,
                    }}
                    toggleClassName={(oldClassname) =>
                      classNames(oldClassname, 'text-main-600')
                    }
                    onChange={(e) => {
                      const date = e?.startDate
                        ? moment(e.startDate).toDate()
                        : null
                      const formatted = date
                        ? moment(date).format('YYYY-MM-DD')
                        : ''
                      setMinDate(date)
                      setFilters((prev) => {
                        const newFilters = new Map(prev)
                        if (formatted) {
                          newFilters.set(
                            ShipmentFilterEnum.START_DATE,
                            formatted,
                          )
                        } else {
                          newFilters.delete(ShipmentFilterEnum.START_DATE)
                        }
                        return newFilters
                      })
                      setFromFilterApi(formatted)
                    }}
                    configs={{
                      shortcuts: {},
                    }}
                  />
                </div>

                <div className="w-full">
                  <FormDatePicker
                    id="shipments-date-to"
                    name="shipments-date-to"
                    label="Hasta"
                    labelClassname="mb-1"
                    useRange={false}
                    minDate={minDate ?? undefined}
                    value={{
                      startDate: maxDate,
                      endDate: maxDate,
                    }}
                    toggleClassName={(oldClassname) =>
                      classNames(oldClassname, 'text-main-600')
                    }
                    onChange={(e) => {
                      const date = e?.startDate
                        ? moment(e.startDate).toDate()
                        : null
                      const formatted = date
                        ? moment(date).format('YYYY-MM-DD')
                        : ''
                      setMaxDate(date)
                      setFilters((prev) => {
                        const newFilters = new Map(prev)
                        if (formatted) {
                          newFilters.set(ShipmentFilterEnum.END_DATE, formatted)
                        } else {
                          newFilters.delete(ShipmentFilterEnum.END_DATE)
                        }
                        return newFilters
                      })
                      setEndFilterApi(formatted)
                    }}
                    configs={{
                      shortcuts: {},
                    }}
                  />
                </div>
              </div>

              {/* Botones de Filtros y Acciones */}
              <div className="flex flex-row items-end justify-between lg:justify-end gap-2 mt-2 lg:mt-0 xl:min-w-fit">
                <div className="flex items-center">
                  <MixedFilterDisclosure
                    showCount
                    showClear
                    title="Filtros"
                    filters={mixedFilters}
                    className="p-0"
                  />
                </div>

                <div className="flex gap-2">
                  <SecondaryButton
                    className="btn-limpiar"
                    title="Limpiar filtros"
                    onClick={handleClearFilters}
                  >
                    <Icon icon={broomIcon} className="w-5 h-5" width="24" />
                  </SecondaryButton>
                  <PrimaryButton onClick={onSearchShipments}>
                    Buscar <MagnifyingGlassIcon className="w-5 h-5 ml-2" />
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col mb-4">
          <div className={classNames(!showFilters && 'hidden')}>
            <ShipmentFilters
              filters={filters}
              setFilters={setFilters}
              setSearchFilter={(e) => setFilterSearchApi(true)}
            />
          </div>
        </div>

        <div className="flex flex-1 justify-between gap-2 md:gap-6 flex-col-reverse md:flex-row mt-2">
          <div className="flex flex-1 justify-between sm:items-center flex-col-reverse gap-6 sm:gap-2 sm:flex-row">
            <div className="flex items-center text-xs">
              <span>Mostrar</span>
              <div className="inline mx-1">
                <FormSelect
                  id="registersNumber"
                  name="registersNumber"
                  label=""
                  selected={registersNumber}
                  options={[10, 25, 50, 100]}
                  className="!w-20 !h-8 !px-2"
                  optionString={(option) => option.toString()}
                  onSelectOption={(option) => setRegistersNumber(option)}
                />
              </div>
              <span>registros</span>
            </div>

            <div className="flex gap-2 items-center">
              <Dropdown
                align="left"
                label="Exportar"
                items={[
                  {
                    items: [
                      {
                        label: 'EXCEL RESUMIDO',
                        icon: (props) => (
                          <Icon icon={fileExcelO} className={props.className} />
                        ),
                        onClick: () => exportShipmentsXLSX(shipments),
                      },
                      {
                        label: 'EXCEL EXTENDIDO',
                        icon: (props) => (
                          <Icon icon={fileExcelO} className={props.className} />
                        ),
                        onClick: () => handleExportReport('EXCEL'),
                      },
                      {
                        label: 'PDF',
                        icon: (props) => (
                          <Icon icon={filePdfO} className={props.className} />
                        ),
                        onClick: () =>
                          exportShipmentsPDF(user.user!, shipments),
                      },
                      {
                        label: 'TXT',
                        icon: (props) => (
                          <Icon icon={fileTextO} className={props.className} />
                        ),
                        onClick: () => exportShipmentsTXT(shipments),
                      },
                    ],
                  },
                ]}
                className=""
                buttonClassName="bg-main-500 hover:bg-main-400 hover:border-main-400 text-white focus-visible:outline-main-400 !pt-1 !pb-1 !text-xs !ring-0 h-[32px] items-center"
                menuClassName="!left-full !top-0 !mt-0 !ml-1 sm:!right-full sm:!left-auto sm:!top-0 sm:!mt-0 sm:!mr-1 sm:!ml-0"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col ">
          <ShipmentTable
            sortBy={sortBy}
            shipments={paginatedShipments}
            selectedShipments={selecteds}
            selectedAll={
              selecteds.length === shipments.length && selecteds.length > 0
            }
            onChangeSortBy={handleSortByChange}
            // Se compara por shipmentHeaderID y no por referencia de objeto: la
            // tabla ya lo hacía así para pintar la fila, y con la selección por
            // rango dejan de fluir las mismas referencias. Comparar por
            // referencia mostraría la fila azul pero no la incluiría al imprimir.
            onSelectShipment={(shipment) => {
              setSelecteds((prev) => toggleSelection(prev, shipment))
            }}
            onSetShipmentsSelected={(targets, selected) => {
              setSelecteds((prev) => mergeSelection(prev, targets, selected))
            }}
            onSelectAll={() => {
              if (selecteds.length === shipments.length) {
                setSelecteds([])
              } else {
                setSelecteds([...shipments])
              }
            }}
            onSuccessAction={onSearchShipments}
          />

          <PaginationFooter
            currentPage={page}
            itemsPerPage={registersNumber}
            totalItems={shipments.length}
            onPageChange={setPage}
          />

          {/* Barra de acciones de selección múltiple.
              Antes: un dropdown "Acciones" que se montaba ARRIBA de la tabla solo
              al seleccionar, empujándola ~56px (el operador perdía de vista la fila
              que acababa de tildar) y cuyo menú se abría tapando las filas
              seleccionadas. Ahora es sticky al fondo: reserva su propio espacio, no
              tapa la tabla, sigue visible al scrollear listas largas, y los dos
              formatos más usados quedan a un solo clic. Mismo patrón que la barra
              de envío de carga masiva (ShipmentBulkUploadReview). */}
          {selecteds.length > 0 && (
            <div className="sticky bottom-0 z-20 -mx-4 mt-6 px-4 py-3 bg-white border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.04)] flex flex-col sm:flex-row sm:items-center gap-3">
              <span className="text-sm font-semibold text-gray-900">
                {selecteds.length === 1
                  ? '1 guía seleccionada'
                  : `${selecteds.length} guías seleccionadas`}
              </span>

              <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
                <PrimaryButton
                  className="!py-1.5 !text-xs items-center gap-1.5"
                  onClick={() => {
                    setLabelDjangoFormat('pdf')
                    // Siempre desde el primer grupo: si quedó en el 3 de una selección
                    // anterior, el visor abriría en un tramo vacío.
                    setLabelBatch(0)
                    setSelectLabelDjango(true)
                  }}
                >
                  <PrinterIcon className="w-4 h-4" />
                  Etiqueta PDF 10x10
                </PrimaryButton>

                <PrimaryButton
                  className="!py-1.5 !text-xs items-center gap-1.5"
                  title="Se imprimen hasta 4 etiquetas por hoja carta para guías multipiezas"
                  onClick={() => {
                    setLabelDjangoFormat('pdf-letter')
                    setLabelBatch(0)
                    setSelectLabelDjango(true)
                  }}
                >
                  <PrinterIcon className="w-4 h-4" />
                  Etiqueta PDF Carta
                </PrimaryButton>

                <Dropdown
                  label="Más"
                  items={[
                    {
                      items: [
                        {
                          label: 'Documento Bolsillo',
                          icon: PrinterIcon,
                          onClick: () => {
                            // Siempre desde la primera tanda: si quedó en la 3 de una
                            // selección anterior, el modal abriría en un tramo vacío.
                            setPocketBatch(0)
                            setSelectPrint(true)
                          },
                        },
                        {
                          label: 'Etiqueta Zebra 10x6',
                          icon: PrinterIcon,
                          onClick: () => {
                            setOpenPrintHelpModal(true)
                            setPrintHelpFormat(0)
                          },
                        },
                        {
                          label: 'Etiqueta Zebra 10x10',
                          icon: PrinterIcon,
                          onClick: () => {
                            setOpenPrintHelpModal(true)
                            setPrintHelpFormat(1)
                          },
                        },
                      ],
                    },
                  ]}
                  buttonClassName="!py-1.5 !text-xs bg-white text-main-500 items-center"
                  // Se abre hacia arriba: la barra vive al fondo del viewport, así
                  // que hacia abajo el menú quedaría fuera de pantalla.
                  menuClassName="!bottom-full !top-auto !mt-0 !mb-2 origin-bottom-right"
                />

                <SecondaryButton
                  className="!py-1.5 !text-xs items-center !border-gray-200 !text-gray-500"
                  onClick={() => setSelecteds([])}
                >
                  Limpiar
                </SecondaryButton>
              </div>
            </div>
          )}

          {selecteds.length > 0 && selectPrint && (
            <ShipmentMultiPrintModal
              open={selectPrint}
              shipment={selecteds.slice(
                pocketBatch * batchSize,
                (pocketBatch + 1) * batchSize,
              )}
              setOpen={setSelectPrint}
              type={0}
              url={'/v1/Shipment/DownloadAllDocumentDeclared'}
              onPrint={() => {}}
              batch={{
                index: pocketBatch,
                total: printGroupCount,
                onChange: setPocketBatch,
              }}
            />
          )}

          {/* Las etiquetas también van por grupos: antes se mandaba la selección entera
              en un solo POST /label y con muchas guías el servicio se quedaba sin tiempo
              (se veía como "Error HTTP  al generar la etiqueta.", sin status). */}
          {selecteds.length > 0 && selectLabelDjango && (
            <ShipmentLabelDjangoModal
              open={selectLabelDjango}
              shipment={selecteds.slice(
                labelBatch * batchSize,
                (labelBatch + 1) * batchSize,
              )}
              setOpen={setSelectLabelDjango}
              format={labelDjangoFormat}
              batch={{
                index: labelBatch,
                total: printGroupCount,
                onChange: setLabelBatch,
              }}
              onTimeout={handleLabelTimeout}
            />
          )}
          <ModalLoading
            open={loading}
            title={loadingTitle}
            status={loadingStatus}
            setOpen={setLoading}
            onPrint={() => {}}
          />
        </div>
      </div>
    </MainPage>
  )
}

export default ConsultShipments
